import React, { useEffect, useState } from "react";
import { t } from "i18next";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud, FiMapPin } from "react-icons/fi";
import { notifyError, notifySuccess } from "../../utils/toast";

const FileUploader = ({
  setFileData,
  fileData,
  fileType = "image", // "image", "video", "document", "location"
  folder = "whatsapp",
  onUploadComplete,
  acceptTypes,
}) => {
  const adminInfo = JSON.parse(localStorage.getItem("adminInfo"));
  const authToken = adminInfo?.token;
  const [file, setFile] = useState(null);
  const [, setLoading] = useState(false);

  const maxFileSizes = {
    image: 5 * 1024 * 1024, // 5 MB
    video: 5 * 1024 * 1024,
    document: 5 * 1024 * 1024,
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept: acceptTypes
      ? acceptTypes
      : fileType === "image"
      ? "image/jpeg, image/jpg, image/png"
      : fileType === "video"
      ? "video/mp4, video/3gp"
      : fileType === "audio"
      ? "audio/mpeg, audio/mp3, audio/aac, audio/ogg"
      : fileType === "document"
      ? "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      : "",
    multiple: false,
    maxSize: maxFileSizes[fileType] || 0,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) setFile(acceptedFiles[0]);
    },
  });

  useEffect(() => {
    if (fileRejections && fileRejections.length > 0) {
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            notifyError(
              `File exceeds max size: ${maxFileSizes[fileType] / 1024 / 1024} MB`
            );
          } else {
            notifyError(`Error uploading "${file.path}": ${error.message}`);
          }
        });
      });
    }

    if (!file) return;

    setLoading(true);

    // For location type: pass coordinates directly
    if (fileType === "location") {
      if (onUploadComplete) onUploadComplete(fileData);
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderName", folder);

    axios({
      url: `${process.env.REACT_APP_API_BASE_URL}/uploadfile/uploads/${folder}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authToken}`,
      },
      data: formData,
    })
      .then((res) => {
        setLoading(false);

        // ⚡ Ensure this is the PUBLIC URL returned from backend
        const uploadedPublicUrl = res?.data?.public_url; // <-- backend should return this key

        if (!uploadedPublicUrl) {
          notifyError(
            "Upload succeeded but backend did not return a public URL!"
          );
          return;
        }

        notifySuccess("File uploaded successfully!");

        if (typeof setFileData === "function") {
          setFileData(uploadedPublicUrl);
        }
        if (onUploadComplete) onUploadComplete(uploadedPublicUrl);
      })
      .catch((err) => {
        console.error("Upload error", err);
        notifyError(err?.message || "Upload failed!");
        setLoading(false);
      });

    return () => {
      if (file?.preview) URL.revokeObjectURL(file.preview);
    };
  }, [file]);

  const renderUploaderContent = () => {
    if (fileType === "location") {
      return (
        <div className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md cursor-pointer px-6 pt-5 pb-6">
          <FiMapPin className="text-3xl text-blue-500 mx-auto" />
          <p className="text-sm mt-2">{t("Enter location coordinates")}</p>
          <em className="text-xs text-gray-400">{t("Latitude and Longitude")}</em>
        </div>
      );
    } else {
      return (
        <div
          className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md cursor-pointer px-6 pt-5 pb-6"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <FiUploadCloud className="text-3xl text-green-500 mx-auto" />
          <p className="text-sm mt-2">{t("Drag & drop your file here")}</p>
          <em className="text-xs text-gray-400">{t("Supported formats vary by type")}</em>
        </div>
      );
    }
  };

  return (
    <div className="w-full text-center">
      {renderUploaderContent()}
      <div className="mt-2 text-xs text-gray-500">
        {fileType !== "location" && (
          <p>Max file size: {maxFileSizes[fileType] / 1024 / 1024} MB.</p>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
